import React, { useState } from "react";
import {
  Container,
  Button,
  TextField,
  Box,
  Typography,
  Divider,
  Tooltip,
  LinearProgress,
} from "@mui/material";
import SnackbarShow from "../SnackbarShow";
import { userBulkEditImport } from "../../API/APICalls";
import Papa from "papaparse";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import ProgressBarShow from "../ProgressBarShow";
import {
  allowedExtensions,
  requiredHeadersForBulkEdit,
  keyPoints,
  sampleBulkEditData,
} from "../../Utils/Constants";
import { createBatches, decideBatchSize } from "../../Utils/HelperFunctions";
import { userEditImportFormValidataion } from "../../Utils/UserValidations";
import UpdateResponseComp from "../UpdateResponseComp";
import ValidationResponseComp from "../ValidationResponseComp";

const UserEditImportForm = ({ id }) => {
  const [snackbarMessage, setSnackbarMessage] = useState({
    open: false,
    message: "",
    severity: "",
  });
  const [progress, setProgress] = useState(0);
  const [uploadResponseMessage, setUploadResponseMessage] = useState([]);
  const [file, setFile] = useState("");
  const [csvData, setCsvData] = useState("");
  const [validationLoading, setValidationLoading] = useState();
  const [validationError, setValidationError] = useState(null);
  const [disableUploadButton, setDisableUploadButton] = useState(true);
  const [disableValidateButton, setDisableValidateButton] = useState(true);

  // File changes handle.
  const handleFileChange = (e) => {
    setFile("");
    setDisableUploadButton(true);
    setDisableValidateButton(true);
    if (e.target.files.length) {
      const inputFile = e.target.files[0];
      if (!allowedExtensions.includes(inputFile?.type)) {
        setSnackbarMessage({
          open: true,
          message: "Please upload a CSV/XLSX file data.",
          severity: "error",
        });
        setDisableValidateButton(true);
        setDisableUploadButton(true);
        return;
      }
      setFile(inputFile);
      setDisableValidateButton(false);
    }
  };

  const validateData = async (data) => {
    const validatedData = [];
    const validationErrors = [];
    if (data === undefined || data === null) {
      setDisableUploadButton(true);
      setDisableValidateButton(true);
      return { validatedData, validationError };
    }
    for (const row of data) {
      try {
        await userEditImportFormValidataion.validate(row, {
          stripUnknown: true,
        });
        if (row.password) {
          row.password = btoa(row.password);
        }
        if (row.mobile) {
          row.mobile = row.mobile.toString();
        }
        if (row.phone) {
          row.phone = row.phone.toString();
        }
        if (typeof row.employee_id === "number") {
          row.employee_id = row.employee_id.toString();
        }
        validatedData.push(row);
      } catch (error) {
        validationErrors.push({
          rowIndex: data.indexOf(row) + 1,
          username: row.local_part,
          errors: Array.isArray(error.errors)
            ? error.errors
            : [String(error.message || error)],
        });
      }
    }
    return { validatedData, validationErrors };
  };

  // Clean CSV file data before validation.
  const cleanUpData = (data) => {
    if (data === undefined || data === null) {
      setDisableUploadButton(true);
      setDisableValidateButton(true);
      return;
    }

    const csvFileHeaders = Object.keys(data[0]);
    const isRequiredHeadersPresent = requiredHeadersForBulkEdit.every(
      (element) => csvFileHeaders.includes(element)
    );

    if (!isRequiredHeadersPresent) {
      setSnackbarMessage({
        open: true,
        message:
          "The imported CSV file header does not match the required header.",
        severity: "error",
      });
      setDisableUploadButton(true);
      setDisableValidateButton(true);
      return;
    }

    const cleanedAndTrimmedData = data
      .filter((row) => {
        return (
          Object.values(row).some(
            (value) => value !== "" && value !== null && value !== undefined
          ) &&
          row["id"] !== undefined &&
          row["id"] !== null &&
          row["id"] !== ""
        );
      })
      .map((row) => {
        const trimmedRow = {};
        for (const key in row) {
          const value = row[key];
          trimmedRow[key] = typeof value === "string" ? value.trim() : value;
        }
        return trimmedRow;
      });

    const filteredData = cleanedAndTrimmedData.map((row) => {
      if (row.country_info !== null && row.country_info !== undefined) {
        if (typeof row.country_info === "number") {
          row.country_info = [row.country_info];
        }
        if (typeof row.country_info === "string") {
          row.country_info = row.country_info
            .split(",")
            .map((num) => Number(num.trim()));
        }
      }

      return Object.fromEntries(
        Object.entries(row).filter(([key]) =>
          requiredHeadersForBulkEdit.includes(key)
        )
      );
    });

    // 4. Ensure uniqueness based on trimmed IDs
    const uniqueParsedData = [
      ...new Map(filteredData.map((item) => [item["id"], item])).values(),
    ];
    return uniqueParsedData;
  };

  // Process CSV file data.
  const processCSVData = (file) => {
    try {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        dynamicTyping: true,
        complete: async (results) => {
          const cleanedData = cleanUpData(results.data);
          const { validatedData, validationErrors } = await validateData(
            cleanedData
          );
          setCsvData(validatedData);
          setValidationError(validationErrors);
          setValidationLoading(false);
          setDisableUploadButton(false);
        },
      });
    } catch (error) {
      console.error(error);
    }
  };

  // Process XLSX file data.
  const processXLSXData = (file) => {
    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, {
          header: 0,
          raw: true,
          defval: null,
        });
        const cleanedData = cleanUpData(jsonData);
        const { validatedData, validationErrors } = await validateData(
          cleanedData
        );
        setCsvData(validatedData);
        setValidationError(validationErrors);
        setValidationLoading(false);
        setDisableUploadButton(false);
      };
      reader.readAsArrayBuffer(file);
    } catch (error) {
      console.error(error);
    }
  };

  const handleValidateButton = () => {
    setDisableUploadButton(true);
    setCsvData("");
    setValidationError("");
    if (!file) {
      setSnackbarMessage({
        open: true,
        message: "Please upload CSV/XLSX file data.",
        severity: "error",
      });
      return;
    }
    setValidationLoading(true);
    const isCSV =
      file.type === "text/csv" || file.name.toLowerCase().endsWith(".csv");

    if (isCSV) {
      processCSVData(file);
    } else {
      processXLSXData(file);
    }
  };

  const handleUploadButton = async () => {
    if (csvData) {
      setValidationError("");
      const sortedData = [...csvData].sort(
        (a, b) => a.allocated_space - b.allocated_space
      );
      const batches = createBatches(
        sortedData,
        decideBatchSize(sortedData.length)
      );
      let completedBatches = 0;
      for (const batch of batches) {
        setDisableUploadButton(true);
        try {
          const response = await userBulkEditImport(id, batch);
          completedBatches += 1;
          const newProgress = (completedBatches / batches.length) * 100;
          setProgress(newProgress);
          setUploadResponseMessage((prevResponses) => [
            ...prevResponses,
            {
              batchId: batches.indexOf(batch) + 1,
              message: response.data || "Batch updated successfully!",
            },
          ]);
        } catch (error) {
          setUploadResponseMessage((prevResponses) => [
            ...prevResponses,
            {
              batchId: batches.indexOf(batch) + 1,
              message: { email: [error.message] },
            },
          ]);
          console.error(error.message);
        } finally {
          setDisableUploadButton(false);
        }
      }
    }
  };

  const handleSampleCsvDownload = () => {
    const csv = Papa.unparse(sampleBulkEditData);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    saveAs(blob, "sample_user_bulk_edit.csv");
  };

  const handleSampleXLSXDownload = () => {
    const worksheet = XLSX.utils.json_to_sheet(sampleBulkEditData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const data = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(data, "sample_user_bulk_edit.xlsx");
  };

  const tipsCSVKeys =
    "To get user id domain_id other details, please export user information and use that csv file to update user details.";

  const snackbarMessageComp = snackbarMessage.open ? (
    <SnackbarShow
      data={snackbarMessage}
      setSnackbarMessage={setSnackbarMessage}
    />
  ) : null;

  const validationLoader = validationLoading ? (
    <Container
      style={{
        gap: 15,
        width: "85vh",
        margin: "0 auto",
        padding: "2rem",
        boxShadow: 3,
      }}
    >
      <LinearProgress />
    </Container>
  ) : null;
  return (
    <>
      {snackbarMessageComp}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 0.5,
        }}
      >
        <Typography variant="h5" gutterBottom>
          Mailbox user bulk edit using CSV/XLSX file.
        </Typography>
        <Typography variant="h6" gutterBottom sx={{ color: "green" }}>
          Key Points for to upload CSV/XLSX file:
        </Typography>
        <Tooltip title={tipsCSVKeys} arrow>
          <ul>
            {keyPoints.map((point, index) => (
              <li key={index}>
                <Typography
                  gutterBottom
                  sx={{ padding: "2px" }}
                  variant="body2"
                >
                  {point}
                </Typography>
                {index !== keyPoints.length - 1 && <Divider />}
              </li>
            ))}
          </ul>
        </Tooltip>
        <Typography variant="body2" gutterBottom sx={{ color: "red" }}>
          Tips : {tipsCSVKeys}
        </Typography>
        <Box>
          <Button
            style={{ margin: "10px" }}
            variant="contained"
            size="small"
            onClick={handleSampleCsvDownload}
          >
            Download Sample CSV
          </Button>
          <Button
            style={{ margin: "10px" }}
            variant="contained"
            size="small"
            onClick={handleSampleXLSXDownload}
          >
            Download Sample XLSX
          </Button>
        </Box>
      </Box>
      <Container
        style={{
          display: "flex",
          flexDirection: "row",
          gap: 15,
          width: "85vh",
          padding: "2rem",
          boxShadow: 3,
        }}
      >
        <TextField
          name="file"
          type="file"
          size="small"
          onChange={handleFileChange}
        />
        <Button
          variant="contained"
          onClick={handleValidateButton}
          disabled={disableValidateButton}
        >
          Validate
        </Button>
        <Button
          variant="contained"
          onClick={handleUploadButton}
          disabled={disableUploadButton}
        >
          Upload
        </Button>
        <Button
          variant="contained"
          onClick={() => {
            window.location.reload();
          }}
        >
          Clear
        </Button>
      </Container>
      {validationLoader}
      {progress > 0 && (
        <Container
          style={{
            display: "flex",
            flexDirection: "row",
            gap: 15,
            width: "85vh",
            margin: "0 auto",
            padding: "2rem",
            boxShadow: 3,
          }}
        >
          <ProgressBarShow progress={progress} />
        </Container>
      )}
      {validationError && (
        <ValidationResponseComp
          data={validationError}
          success_count={csvData.length}
        />
      )}
      {uploadResponseMessage.length > 0 && (
        <UpdateResponseComp data={uploadResponseMessage} />
      )}
    </>
  );
};

export default UserEditImportForm;
